/** État de la timeline multipiste (studio). Modèle projet = tempo commun + pistes × clips. */
import { useReducer } from 'react';
import { uid } from '../format';

export interface Clip {
  id: string;
  name: string;
  buffer: AudioBuffer;
  start: number;    // position sur la timeline (s)
  offset: number;   // début dans le buffer (s)
  duration: number; // durée jouée (s)
}

export interface Track {
  id: string;
  name: string;
  volume: number;   // 0..1
  muted: boolean;
  solo: boolean;
  clips: Clip[];
}

export interface StudioState {
  bpm: number;
  beatsPerBar: number;
  tracks: Track[];
  pxPerSec: number;   // zoom horizontal
  scroll: number;     // décalage gauche (s)
  playhead: number;   // position de lecture (s)
  selectedClip: string | null;
  selectedTrack: string | null;
}

export const TRACK_COLORS = ['#ff6b6b', '#c86bff', '#3ddc84', '#ffb84d', '#4db8ff', '#ff6bd0', '#9bd84d', '#4de0d0'];

function newTrack(n: number, name?: string, id?: string): Track {
  return { id: id ?? uid(), name: name ?? `Piste ${n}`, volume: 0.9, muted: false, solo: false, clips: [] };
}

export const initialStudio: StudioState = {
  bpm: 90,
  beatsPerBar: 4,
  tracks: [newTrack(1, 'Piste principale')],
  pxPerSec: 80,
  scroll: 0,
  playhead: 0,
  selectedClip: null,
  selectedTrack: null,
};

export type StudioAction =
  | { type: 'bpm'; bpm: number }
  | { type: 'beatsPerBar'; n: number }
  | { type: 'track/add'; name?: string; id?: string }
  | { type: 'track/remove'; id: string }
  | { type: 'track/patch'; id: string; patch: Partial<Pick<Track, 'name' | 'volume' | 'muted' | 'solo'>> }
  | { type: 'clip/add'; trackId: string; clip: Omit<Clip, 'id'>; select?: boolean }
  | { type: 'clip/move'; id: string; toTrackId: string; start: number }
  | { type: 'clip/trim'; id: string; side: 'start' | 'end'; delta: number }
  | { type: 'clip/split'; id: string; at: number }
  | { type: 'clip/remove'; id: string }
  | { type: 'clip/replace'; id: string; buffer: AudioBuffer; offset: number; duration: number }
  | { type: 'clip/select'; id: string | null }
  | { type: 'track/select'; id: string | null }
  | { type: 'view'; pxPerSec?: number; scroll?: number }
  | { type: 'playhead'; pos: number }
  | { type: 'tracks/set'; tracks: Track[] }
  | { type: 'track/bounce'; id: string; buffer: AudioBuffer; name?: string }
  | { type: 'merge'; buffer: AudioBuffer }
  | { type: 'reset' };

function findClip(tracks: Track[], id: string): { track: Track; clip: Clip } | null {
  for (const track of tracks) {
    const clip = track.clips.find((c) => c.id === id);
    if (clip) return { track, clip };
  }
  return null;
}

export function studioReducer(s: StudioState, a: StudioAction): StudioState {
  switch (a.type) {
    case 'bpm':
      return { ...s, bpm: Math.max(30, Math.min(300, a.bpm)) };
    case 'beatsPerBar':
      return { ...s, beatsPerBar: Math.max(1, Math.min(12, a.n)) };

    case 'track/add':
      return { ...s, tracks: [...s.tracks, newTrack(s.tracks.length + 1, a.name, a.id)] };

    case 'track/remove': {
      const removed = s.tracks.find((t) => t.id === a.id);
      const tracks = s.tracks.filter((t) => t.id !== a.id);
      const clipGone = removed?.clips.some((c) => c.id === s.selectedClip) ?? false;
      return {
        ...s,
        tracks: tracks.length ? tracks : [newTrack(1, 'Piste principale')],
        selectedTrack: s.selectedTrack === a.id ? null : s.selectedTrack,
        selectedClip: clipGone ? null : s.selectedClip,
      };
    }

    case 'track/patch':
      return { ...s, tracks: s.tracks.map((t) => (t.id === a.id ? { ...t, ...a.patch } : t)) };

    case 'track/select':
      return { ...s, selectedTrack: a.id };

    case 'clip/add': {
      // si la piste visée a disparu entre-temps (supprimée pendant un
      // enregistrement/import), on retombe sur la première piste plutôt
      // que de perdre l'audio en silence
      const target = s.tracks.some((t) => t.id === a.trackId) ? a.trackId : s.tracks[0]?.id;
      if (!target) return s;
      const clip: Clip = { ...a.clip, id: uid() };
      return {
        ...s,
        tracks: s.tracks.map((t) => (t.id === target ? { ...t, clips: [...t.clips, clip] } : t)),
        selectedClip: a.select ? clip.id : s.selectedClip,
      };
    }

    case 'clip/move': {
      const found = findClip(s.tracks, a.id);
      if (!found) return s;
      const moved: Clip = { ...found.clip, start: Math.max(0, a.start) };
      return {
        ...s,
        tracks: s.tracks.map((t) => {
          let clips = t.clips.filter((c) => c.id !== a.id);
          if (t.id === a.toTrackId) clips = [...clips, moved];
          return clips === t.clips ? t : { ...t, clips };
        }),
      };
    }

    case 'clip/trim': {
      const found = findClip(s.tracks, a.id);
      if (!found) return s;
      const c = found.clip;
      let { start, offset, duration } = c;
      const bufDur = c.buffer.duration;
      if (a.side === 'start') {
        // borne : on ne dépasse pas le début du buffer, ni ne réduit sous 50 ms
        // (le min d'abord, le max ensuite : garantit d ≥ -offset/-start même
        // pour un clip plus court que 50 ms — sinon start/offset négatifs)
        let d = a.delta;
        d = Math.min(d, duration - 0.05);
        d = Math.max(d, -offset, -start);
        start += d; offset += d; duration -= d;
      } else {
        let d = a.delta;
        d = Math.max(d, 0.05 - duration);
        d = Math.min(d, bufDur - offset - duration);
        duration += d;
      }
      const updated: Clip = { ...c, start, offset, duration };
      return {
        ...s,
        tracks: s.tracks.map((t) =>
          t.id === found.track.id ? { ...t, clips: t.clips.map((x) => (x.id === a.id ? updated : x)) } : t,
        ),
      };
    }

    case 'clip/split': {
      // coupe un clip en deux à la position `at` (temps timeline)
      const found = findClip(s.tracks, a.id);
      if (!found) return s;
      const c = found.clip;
      const rel = a.at - c.start; // position de coupe relative au clip
      if (rel < 0.05 || rel > c.duration - 0.05) return s; // trop près d'un bord
      const left: Clip = { ...c, duration: rel };
      const right: Clip = {
        id: uid(), name: c.name, buffer: c.buffer,
        start: c.start + rel, offset: c.offset + rel, duration: c.duration - rel,
      };
      return {
        ...s,
        tracks: s.tracks.map((t) =>
          t.id === found.track.id
            ? { ...t, clips: t.clips.flatMap((x) => (x.id === a.id ? [left, right] : [x])) }
            : t,
        ),
        selectedClip: right.id,
      };
    }

    case 'clip/replace': {
      const found = findClip(s.tracks, a.id);
      if (!found) return s;
      const updated: Clip = { ...found.clip, buffer: a.buffer, offset: a.offset, duration: a.duration };
      return {
        ...s,
        tracks: s.tracks.map((t) =>
          t.id === found.track.id ? { ...t, clips: t.clips.map((x) => (x.id === a.id ? updated : x)) } : t,
        ),
      };
    }

    case 'clip/remove':
      return {
        ...s,
        tracks: s.tracks.map((t) => ({ ...t, clips: t.clips.filter((c) => c.id !== a.id) })),
        selectedClip: s.selectedClip === a.id ? null : s.selectedClip,
      };

    case 'clip/select':
      return { ...s, selectedClip: a.id };

    case 'view':
      return {
        ...s,
        pxPerSec: a.pxPerSec != null ? Math.max(8, Math.min(600, a.pxPerSec)) : s.pxPerSec,
        scroll: a.scroll != null ? Math.max(0, a.scroll) : s.scroll,
      };

    case 'playhead':
      return { ...s, playhead: Math.max(0, a.pos) };

    case 'tracks/set':
      return { ...s, tracks: a.tracks };

    case 'track/bounce': {
      // remplace tous les clips d'une piste par un seul (résultat d'un traitement/rendu)
      if (!s.tracks.some((t) => t.id === a.id)) return s; // piste supprimée entre-temps
      const clip: Clip = { id: uid(), name: a.name ?? 'rendu', buffer: a.buffer, start: 0, offset: 0, duration: a.buffer.duration };
      return {
        ...s,
        tracks: s.tracks.map((t) => (t.id === a.id ? { ...t, clips: [clip] } : t)),
        selectedClip: clip.id,
      };
    }

    case 'merge': {
      // fusionne toutes les pistes audibles en une seule
      const t = newTrack(1, 'Mix');
      t.clips = [{ id: uid(), name: 'Mix', buffer: a.buffer, start: 0, offset: 0, duration: a.buffer.duration }];
      return { ...s, tracks: [t], selectedClip: t.clips[0].id, selectedTrack: t.id };
    }

    case 'reset':
      return { ...initialStudio, tracks: [newTrack(1, 'Piste principale')], bpm: s.bpm };

    default:
      return s;
  }
}

export function useStudio() {
  return useReducer(studioReducer, initialStudio);
}
